import { Link } from "@tanstack/react-router";
import { useLayoutEffect, useRef } from "react";
import { cn } from "../../lib/cn.js";
import {
  formatAge,
  formatPrice,
  formatSignedPct,
  istClock,
  istDate,
  istDateTime,
} from "../../lib/format.js";
import type { SignalTrade, SignalView, TradeHorizon } from "../../lib/queries.js";
import { openSignalModal } from "../../lib/signalModalStore.js";
import { derivedVotes, votesSentence } from "./derivedVotes.js";
import { SEVERITY_TEXT } from "./severity.js";
import { isPlainClick } from "./signalRowClick.js";

/** Long is the up colour, short is the down colour. Same pair everywhere. */
const SIDE_CLASS = {
  long: "text-up",
  short: "text-down",
} as const;

export function SideCell({ trade }: { trade: SignalTrade | null }) {
  if (trade === null) return <span className="text-text-muted">—</span>;
  const side = trade.plan.side;
  return (
    <span className={cn("text-xs font-semibold uppercase", SIDE_CLASS[side])}>
      {side === "long" ? "Long" : "Short"}
    </span>
  );
}

/**
 * How long the strategy behind the row means to hold.
 *
 * Its own column beside Side rather than a note on the Family one, because
 * Family answers a different question and answering both in one cell is how
 * the two got confused in the first place. Family names the job that wrote the
 * row: `prophet_swing` says "intraday" there and holds for days.
 *
 * Muted, and quieter than Side. A reader scanning the feed is asking "which
 * way" first and "for how long" second, and a horizon in the same weight as
 * the side would compete with it.
 */
export function HorizonCell({ horizon, id }: { horizon: TradeHorizon | null; id: string }) {
  return (
    <span className="text-xs text-text-muted" data-testid={`horizon-${id}`}>
      {horizon ?? "\u2014"}
    </span>
  );
}

export function SeverityCell({ severity }: { severity: string }) {
  return (
    <span className={cn("text-xs font-medium uppercase", SEVERITY_TEXT[severity])}>{severity}</span>
  );
}

/**
 * When it formed, to the minute, and how long ago.
 *
 * Both, because they answer different questions. The clock is what a reader
 * matches against their own chart; the age is what tells them whether the
 * setup is still worth looking at.
 */
export function FormedCell({ signal, now }: { signal: SignalView; now: Date }) {
  return (
    <span className="flex items-baseline gap-2">
      <time dateTime={signal.occurredAt} className="tabular-nums">
        {istClock(signal.occurredAt)}
      </time>
      <span className="text-xs text-text-muted tabular-nums">
        {formatAge(signal.occurredAt, now)}
      </span>
    </span>
  );
}

/**
 * The signal name, as a real link to its own page that opens the modal instead.
 *
 * A `<tr>` cannot be an anchor, so the row's keyboard path lives here: Tab
 * reaches this link, Enter opens the modal, and cmd-click or middle-click
 * opens `/signals/$signalId` in a new tab the way the cards did. Without it
 * the modal was mouse-only and the detail route was unreachable from the feed.
 *
 * A plain click is the one case the modal takes over, because a reader
 * scanning a hundred rows should not lose their place to see one of them.
 */
export function SignalLinkCell({ signal, label }: { signal: SignalView; label: string }) {
  return (
    <span className="flex items-baseline gap-1">
      <Link
        to="/signals/$signalId"
        params={{ signalId: signal.id }}
        title={signal.explanation.sentence}
        className="truncate hover:text-accent hover:underline"
        onClick={(event) => {
          if (!isPlainClick(event)) return;
          event.preventDefault();
          openSignalModal(signal.id, event.currentTarget);
        }}
      >
        {label}
      </Link>
      <DerivedMark signal={signal} />
    </span>
  );
}

/**
 * That this row restates ones already in the table, and how many.
 *
 * Confluence is scored from the votes of strategies that each wrote their own
 * row a moment earlier. Unmarked, "three or more strategies agreeing" reads as
 * a fourth observation stacked on the three above it — which is how CASTROLIND
 * showed five short rows on 2026-09-07 for three bearish votes in one bucket.
 *
 * Beside the name rather than in a column of its own: it applies to four
 * signal types out of forty, and a column that is a dash on every other row
 * costs every reader width to tell almost none of them anything.
 */
function DerivedMark({ signal }: { signal: SignalView }) {
  if (!signal.derived) return null;
  const votes = derivedVotes(signal.payload);
  const sentence = votesSentence(votes);
  return (
    <span
      className="shrink-0 text-xs text-text-muted"
      data-testid={`derived-${signal.id}`}
      title={
        sentence === ""
          ? "Scored from rows already in this table, not a separate observation."
          : `Scored from rows already in this table: ${sentence}.`
      }
    >
      {votes.length > 0 ? `\u21b3${votes.length}` : "\u21b3"}
    </span>
  );
}

/**
 * How old a snapshot has to be before its price stops meaning "now".
 *
 * The quote job runs every minute between 09:15 and 15:30 IST, so five
 * minutes is five missed runs: a stopped pipeline, or a shut market.
 */
const STALE_MINUTES = 5;

/**
 * The last traded price, carrying the age of the snapshot it came from.
 *
 * This one number feeds Since, R, To target, and Status, so an unmarked
 * Friday-afternoon quote read on Sunday puts five live-looking numbers on the
 * screen. The age says which rows are current; the title gives the exact
 * stamp, the way the symbol page's quote card does.
 */
export function LastCell({ quote, now }: { quote: SignalView["quote"]; now: Date }) {
  if (quote === null) return <span className="text-text-muted">—</span>;

  const fetchedAt = new Date(quote.fetchedAt);
  const stale = now.getTime() - fetchedAt.getTime() >= STALE_MINUTES * 60_000;

  return (
    <span
      className="flex items-baseline justify-end gap-1"
      title={`Quoted ${istDateTime(quote.fetchedAt)}`}
    >
      <span className={cn("tabular-nums", stale && "text-text-muted")}>
        {formatPrice(quote.lastPrice)}
      </span>
      {stale ? (
        <span className="text-xs text-warning tabular-nums">{formatAge(quote.fetchedAt, now)}</span>
      ) : null}
    </span>
  );
}

export function SessionCell({ sessionDate }: { sessionDate: string }) {
  return <span className="text-xs text-text-muted tabular-nums">{istDate(sessionDate)}</span>;
}

export function SymbolCell({ symbol }: { symbol: string }) {
  return (
    <Link
      to="/symbol/$symbol"
      params={{ symbol }}
      className="font-medium hover:text-accent hover:underline"
    >
      {symbol}
    </Link>
  );
}

const STATUS_LABEL = {
  target: "Target",
  stopped: "Stopped",
  open: "Open",
} as const;

const STATUS_CLASS = {
  target: "border-up/50 bg-up/15 text-up",
  stopped: "border-danger/50 bg-danger/15 text-danger",
  open: "border-border bg-surface text-text-muted",
} as const;

/**
 * Where the last print sits relative to the levels.
 *
 * Read off one price, not the path: a name that traded through its stop at
 * 13:00 and recovered by 15:15 reads "Open" here, because nothing in this feed
 * stores the path. The title says so rather than leaving it implied.
 */
export function StatusCell({ trade }: { trade: SignalTrade | null }) {
  const status = trade?.progress?.status;
  if (status === undefined) return <span className="text-text-muted">—</span>;
  return (
    <span
      title="Read off the last traded price, not the path — an intraday touch that recovered still reads Open"
      className={cn("border px-1.5 py-0.5 text-xs", STATUS_CLASS[status])}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

/**
 * The move since formation, signed to the trade's side and coloured by it.
 *
 * Signed to the side is the point: a short that fell 2% is up 2%, and a column
 * that printed -2% for it would be read as a loss.
 */
export function ReturnCell({ trade }: { trade: SignalTrade | null }) {
  const returnPct = trade?.progress?.returnPct;
  if (returnPct === undefined) return <span className="text-text-muted">—</span>;
  return (
    <span className={cn("tabular-nums", returnPct >= 0 ? "text-up" : "text-down")}>
      {formatSignedPct(returnPct)}
    </span>
  );
}

/** Clamped to the bar's width; the number beside it is not clamped. */
function clamp(value: number): number {
  return Math.max(0, Math.min(100, value));
}

/**
 * How far between entry and target the price has travelled.
 *
 * A bar rather than a number alone, because the whole column is then scannable
 * at arm's length: this is the one metric a reader uses to pick which of forty
 * open ideas to look at first. Past the entry the bar fills toward the target;
 * the other way it fills red toward the stop.
 */
export function ProgressCell({ trade }: { trade: SignalTrade | null }) {
  const pct = trade?.progress?.targetProgressPct ?? null;
  return pct === null ? <span className="text-xs text-text-muted">—</span> : <Meter pct={pct} />;
}

/**
 * The bar itself, split out because it holds a hook and `ProgressCell` returns
 * early for the rows that have no target.
 *
 * The width is written to the node in a layout effect rather than through an
 * inline `style` attribute: Tailwind's JIT cannot generate a class from a
 * percentage computed at run time (Global Constraint 19). A ref callback would
 * also work, but only by accident — an inline callback is a new function every
 * render, so React detaches and reattaches it each time. An effect says what it
 * means and runs when the number changes.
 */
function Meter({ pct }: { pct: number }) {
  const fill = useRef<HTMLSpanElement>(null);
  const width = clamp(Math.abs(pct));

  useLayoutEffect(() => {
    fill.current?.style.setProperty("width", `${width}%`);
  }, [width]);

  return (
    <span className="flex items-center gap-2" title={`${pct.toFixed(0)}% of the way to target`}>
      <span className="h-1.5 w-14 overflow-hidden bg-border">
        <span ref={fill} className={cn("block h-full", pct >= 0 ? "bg-up" : "bg-down")} />
      </span>
      <span className="w-10 text-right text-xs text-text-muted tabular-nums">
        {pct.toFixed(0)}%
      </span>
    </span>
  );
}
